
import { Component, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { resolveLocale } from '@/utils/i18n-helpers';
import InfoBanner from '../shared/suites/InfoBanner.vue';
import TextButton from '../shared/suites/TextButton.vue';
import RestaurantSelector from '../shared/RestaurantSelector.vue';
import TextField from '@/components/shared/TextField.vue';
import SelectComponent from '../shared/Select.vue';

const namespace: string = 'auth';

@Component<CreateAnnouncement>({
	components: {
		TextField,
		InfoBanner,
		TextButton,
		RestaurantSelector,
		SelectComponent
	}
})
export default class CreateAnnouncement extends Vue {
	@Getter('getRestaurants', { namespace }) private restaurants!: Tenant[];
	@Getter('getTenantId', { namespace }) private tenantId!: string;

	$refs!: { messageInput: any; searchInput: any; };

	private announcement: AnnouncementPayload = {
		message: {},
		restaurantIds: []
	};
	private initialLocale: string = '';
	private messageError: string = '';
	private restaurantError: string = '';
	private selectedLocale: RestaurantLocale = {} as RestaurantLocale;
	private locales: RestaurantLocale[] = [];

	private created(): void {
		// Get the list of locales from the restaurants the user has access to
		// There will always be at least one locale in the list (English)
		this.locales = this.getLocalesFromRestaurants();
		this.selectedLocale = this.setSelectedLocale();

		// Set the inital locale for displaying the message placeholader
		this.initialLocale = this.selectedLocale.locale_short;

		// Format the announcement message object to include all locales
		this.locales.forEach((locale: RestaurantLocale) => {
			if (!this.announcement.message?.locale) {
				this.announcement.message[locale.locale_short] = '';
			}
		});
	}

	/**
	 * Set locales to a list of all unique locales from the restaurants the user has access to
	 * Since the default locale for restaurants can vary, no default locale is set here
	 * This will always return at least one locale because if a restaurant doesn't have any locales set, then English will be added
	 *
	 * @return {RestaurantLocale[]}
	 */
	private getLocalesFromRestaurants(): RestaurantLocale[] {
		return this.restaurants.reduce((acc: RestaurantLocale[], restaurant: Tenant) => {
			const locales = restaurant.locales || [];
			// If a restaurant doesn't have any locales set, the assumption is that it only supports English so we add it to the list
			if (!locales.length && !acc.find((l: RestaurantLocale) => l.locale_short === 'en')) {
				acc.push({ locale_short: 'en', locale_long: 'English', label: 'English', is_default: false });
			}
			locales.forEach((locale: RestaurantLocale) => {
				if (!acc.find((l: RestaurantLocale) => l.locale_short === locale.locale_short)) {
					acc.push({ ...locale, is_default: false });
				}
			});
			return acc;
		}, []).sort((a: RestaurantLocale, b: RestaurantLocale) => a.locale_long.localeCompare(b.locale_long));
	}

	/**
	 * Set the selected locale from the list of locales
	 *
	 * @return {RestaurantLocale}
	 */
	private setSelectedLocale(): RestaurantLocale {
		// If there is only one locale, set it as the selected locale
		if (this.locales.length === 1) {
			return this.locales[0];
		}
		// If the user's browser locale is in the list, set it as the selected locale
		const currentLocale = resolveLocale(this.$i18n.locale, this.locales);
		const currentLocaleIndex = this.locales.findIndex((locale: RestaurantLocale) => locale.locale_short === currentLocale);
		if (currentLocaleIndex > -1) {
			return this.locales[currentLocaleIndex];
		}
		// Otherwise set English as the selected locale
		const englishLocaleIndex = this.locales.findIndex((locale: RestaurantLocale) => locale.locale_short === 'en');
		if (englishLocaleIndex > -1) {
			return this.locales[englishLocaleIndex];
		}
		// Otherwise set the first locale as the selected locale
		return this.locales[0];
	}

	/**
	 * Focus the message input when the locale is changed
	 *
	 * @return {void}
	 */
	private focusMessageInput(): void {
		this.$nextTick(() => {
			this.$refs.messageInput.focus();
		});
	}

	/**
	 * Validate announcement form
	 * All locales must have a message and at least one restaurant must be selected
	 * Display error messages if validation fails
	 *
	 * @return {boolean}
	 */
	public validateAnnouncement(): boolean {
		const allLocalesHaveMessage = Object.values(this.announcement.message).every((message: string) => message);
		const hasRestaurants = this.announcement.restaurantIds.length > 0;

		if (!allLocalesHaveMessage) {
			this.messageError = this.$t('settings.locations.announcements.create.error_message_required');
		}
		if (!hasRestaurants) {
			this.restaurantError = this.$t('settings.locations.announcements.create.error_locations_required');
		}

		return allLocalesHaveMessage && hasRestaurants;
	}
}
